import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import inviteLeftIcon from "@/assets/images/invite_left.png";
import inviteRightIcon from "@/assets/images/invite_right.png";
import walletIcon from "@/assets/images/wallet.png";
import walletConnectedIcon from "@/assets/images/wallet_connected.png";
import { Icon } from "@/components/icon";
import Logo from "@/components/logo";
import { NavMini, NavVertical } from "@/components/nav";
import type { NavProps } from "@/components/nav/types";
import ContributionDegreeDialog from "@/components/portfolio/ContributionDegreeDialog";
import { GLOBAL_CONFIG } from "@/global-config";
import useLocale, { LANGUAGE_MAP } from "@/locales/use-locale";
import { useWalletAuth } from "@/routes/hooks/useWalletAuth";
import { useSettingActions, useSettings } from "@/store/settingStore";
import { ThemeLayout } from "@/types/enum";
import { Button } from "@/ui/button";
import { ScrollArea } from "@/ui/scroll-area";
import { cn } from "@/utils";

type Props = {
	data: NavProps["data"];
	className?: string;
};

export function NavVerticalLayout({ data, className }: Props) {
	const settings = useSettings();
	const { themeLayout } = settings;
	const { setSettings } = useSettingActions();
	const { t } = useTranslation();
	const { locale, language, setLocale } = useLocale();
	const navigate = useNavigate();
	const { connected, publicKey, connectAndAuthenticate, logout, isLoading } = useWalletAuth();
	const [dropdownVisible, setDropdownVisible] = useState(false);
	const [languageMenuVisible, setLanguageMenuVisible] = useState(false);
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

	const handleSignIn = async () => {
		try {
			await connectAndAuthenticate();
		} catch (error) {
			console.error("Sign in failed:", error);
		}
	};

	const handleDisconnect = async () => {
		try {
			await logout();
		} catch (error) {
			console.error("Disconnect failed:", error);
		}
		setDropdownVisible(false);
	};
	useEffect(() => {
		if (themeLayout === ThemeLayout.Mini) {
			setSettings({
				...settings,
				themeLayout: ThemeLayout.Vertical,
			});
		}
	}, [themeLayout, settings, setSettings]);
	// 关闭下拉菜单当点击外部时
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (
				(dropdownVisible || languageMenuVisible) &&
				!target.closest(".wallet-dropdown") &&
				!target.closest(".language-menu")
			) {
				setDropdownVisible(false);
				setLanguageMenuVisible(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [dropdownVisible, languageMenuVisible]);

	const navWidth = themeLayout === ThemeLayout.Vertical ? "var(--layout-nav-width)" : "var(--layout-nav-width-mini)";

	return (
		<nav
			data-slot="slash-layout-nav"
			className={cn(
				"fixed inset-y-0 left-0 flex-col h-full bg-background border-r border-dashed transition-[width] duration-300 ease-in-out !z-[9999]",
				className,
			)}
			style={{
				width: navWidth,
			}}
		>
			<div
				className={cn("relative flex items-center py-4 px-2 h-[var(--layout-header-height)] z-[9999] ", {
					"justify-center": themeLayout === ThemeLayout.Mini,
				})}
			>
				<div className="flex items-center justify-center">
					<Logo />
					<span
						className="text-xl font-bold transition-all duration-300 ease-in-out"
						style={{
							opacity: themeLayout === ThemeLayout.Mini ? 0 : 1,
							maxWidth: themeLayout === ThemeLayout.Mini ? 0 : "auto",
							whiteSpace: "nowrap",
							marginLeft: themeLayout === ThemeLayout.Mini ? 0 : "8px",
						}}
					>
						{GLOBAL_CONFIG.appName}
					</span>
				</div>
			</div>

			<ScrollArea className={cn("h-[calc(100vh-var(--layout-header-height)-210px)] px-2 bg-[#F9FAFB]")}>
				{themeLayout === ThemeLayout.Mini ? <NavMini data={data} /> : <NavVertical data={data} />}
			</ScrollArea>

			{/* Invite Friends Button - 在钱包连接按钮上方 */}
			<div className="px-2 pb-2 mb-4 flex-shrink-0">
				<button
					type="button"
					onClick={() => setIsInviteDialogOpen(true)}
					className="flex items-center justify-center gap-3 w-full bg-[#7BDF00] text-white rounded-[8px] relative p-3 transition-all duration-200 shadow-md hover:shadow-lg"
				>
					<img
						src={inviteLeftIcon}
						alt="Gift"
						className="absolute left-[-20px] w-[120px] top-[-20px] pointer-events-auto"
					/>
					<span className="text-sm font-bold uppercase">{t("portfolio.invite.buttonText")}</span>
					<img src={inviteRightIcon} alt="" className=" h-full absolute right-0" />
				</button>
			</div>
			<div className=" language-menu px-2 pb-4 z-[20] relative">
				<button
					type="button"
					onClick={() => {
						setLanguageMenuVisible(!languageMenuVisible);
					}}
					className="flex items-center justify-between w-full text-left px-3 py-3 rounded-[4px] hover:bg-gray-50 transition-colors duration-200"
				>
					<div className="flex items-center gap-3">
						<Icon icon="mdi:earth" size={20} className="text-gray-700" />
						<span className="text-sm text-gray-900 font-medium">Language</span>
					</div>
					<span className="text-sm text-gray-600">{language.label}</span>
				</button>

				{/* Language Submenu */}
				{languageMenuVisible && (
					<div className="absolute right-0 top-[-120px] mr-2 bg-white rounded-[8px] shadow-xl z-[60] overflow-hidden p-2 min-w-[140px]">
						{Object.values(LANGUAGE_MAP).map((lang) => (
							<button
								key={lang.locale}
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setLocale(lang.locale);
									setLanguageMenuVisible(false);
									setDropdownVisible(false);
								}}
								className={cn(
									"flex items-center gap-2 w-full text-left px-3 py-2 rounded-[4px] hover:bg-gray-50 transition-colors duration-200",
									locale === lang.locale && "bg-gray-100",
								)}
							>
								<span className="text-sm text-gray-900">{lang.label}</span>
							</button>
						))}
					</div>
				)}
			</div>
			{/* Wallet Connection Section */}
			<div className="px-2 pb-4 z-[20] relative">
				<div className="flex flex-col items-center space-y-2">
					{!connected ? (
						<div className="flex items-center justify-between w-full bg-black rounded-[4px] p-2">
							<Button
								className="bg-transparent hover:bg-transparent text-[#fff] px-3 py-2.5 rounded-full font-medium transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full justify-start border-none shadow-none"
								onClick={handleSignIn}
								disabled={isLoading}
							>
								{isLoading ? t("wallet.connecting") : t("wallet.signIn")}
							</Button>
							<img src={walletIcon} alt="" className="w-[28px] h-[28px]" />
						</div>
					) : (
						<div className="relative wallet-dropdown flex w-full flex-col gap-2">
							{dropdownVisible && (
								<div className="absolute right-0 bottom-full mb-2 w-full bg-white rounded-[8px] shadow-xl z-50 overflow-hidden p-2">
									{/* Profile */}
									<button
										type="button"
										onClick={() => {
											navigate("/profile");
											setDropdownVisible(false);
										}}
										className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-[4px] hover:bg-gray-50 transition-colors duration-200"
									>
										<Icon icon="mdi:account-outline" size={20} className="text-gray-700" />
										<span className="text-sm text-gray-900 font-medium">Profile</span>
									</button>

									{/* Language */}

									{/* Log out */}
									<button
										type="button"
										onClick={handleDisconnect}
										className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-[4px] hover:bg-gray-50 transition-colors duration-200"
									>
										<Icon icon="mdi:logout" size={20} className="text-red-500" />
										<span className="text-sm text-red-500 font-medium">Log out</span>
									</button>
								</div>
							)}

							{/* Wallet Address Display */}
							<div
								className="flex w-full p-3 bg-white items-center gap-3 rounded-[8px] cursor-pointer hover:bg-gray-50 transition-colors"
								onClick={(e) => {
									e.stopPropagation();
									setDropdownVisible(!dropdownVisible);
									setLanguageMenuVisible(false);
								}}
							>
								{publicKey && (
									<>
										<img src={walletConnectedIcon} alt="" className="w-[28px] h-[28px] rounded-full" />
										<span className="text-sm font-medium text-gray-900 flex-1">
											{`${publicKey.toBase58().slice(0, 6)}...${publicKey.toBase58().slice(-4)}`}
										</span>
									</>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Contribution Degree Dialog */}
			<ContributionDegreeDialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen} />
		</nav>
	);
}
